'use client';

import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { usePrivy, useWallets } from '@privy-io/react-auth';

import { useAuth, useClobCredentials, usePolymarketClient, useTokenApprovals } from '@/hooks';
import { walletApi } from '@/services/api';
import { clobApi } from '@/services/api/clob.api';
import { useAppDispatch } from '@/store';
import {
  setIsReadyToTrade,
  setProxyError,
  setProxyInitializing,
  setSetupRequested,
  updateUser,
} from '@/store/slices/auth.slice';
import type { TradingWalletKind } from '@/types/auth.types';
import { normalizeClobAddress } from '@/utils/clob.utils';
import {
  clearStoredClobBinding,
  readStoredClobBinding,
} from '@/utils/clobCredentialBindingStorage';
import { deriveSafe } from '@/utils/polymarket.utils';

import { MESSAGES } from '@/constants/messages.constants';
import { NATIVE_USDC_CONTRACT_ADDRESS } from '@/constants/tokens.constants';

/**
 * setupStep represents the granular lifecycle of the proxy wallet initialization.
 */
export type ProxySetupStep =
  | 'idle'
  | 'prefetching'
  | 'deploying'
  | 'registering'
  | 'provisioning'
  | 'approving'
  | 'syncing'
  | 'complete'
  | 'error';

/**
 * useProxyInitialization
 *
 * Orchestrates Polymarket trading wallet setup:
 * - Legacy users: Gnosis Safe + POLY_GNOSIS_SAFE CLOB orders
 * - New users (Polymarket): ERC-4337 deposit wallet + POLY_1271 — required when Safe makers are rejected
 */
export function useProxyInitialization() {
  const dispatch = useAppDispatch();
  const [setupStep, setSetupStep] = useState<ProxySetupStep>('idle');

  // TEMP breadcrumb: logs every step transition so we can see the last step
  // reached before a hang on UAT. Remove once proxy setup is confirmed working.
  useEffect(() => {
    console.info('[ProxyInit] step →', setupStep);
  }, [setupStep]);

  const { user, isProxyInitializing: isSystemInitializing } = useAuth();
  const { user: privyUser } = usePrivy();
  const { wallets } = useWallets();
  const { client, signer } = usePolymarketClient();
  const { credentials, provisionCredentials, refetchStatus, resetCredentials } =
    useClobCredentials();
  const { ensureAllTokenApprovals, checkAllTokenApprovals } = useTokenApprovals();

  const linkedWallet = wallets.find(
    (wallet) => wallet.address?.toLowerCase() === user?.walletAddress?.toLowerCase()
  );
  const embeddedWallet = wallets.find((wallet) => wallet.walletClientType === 'privy');
  const eoaAddress =
    user?.walletAddress ||
    linkedWallet?.address ||
    embeddedWallet?.address ||
    privyUser?.wallet?.address;

  const existingProxyAddress = user?.proxyWalletAddress;

  const syncCollateralBalance = async (
    funderAddress: string,
    creds: any,
    kind: TradingWalletKind
  ) => {
    if (!signer) return;

    setSetupStep('syncing');
    try {
      const { ClobClient, SignatureTypeV2 } = await import('@polymarket/clob-client-v2');
      const { CLOB_API_URL, POLY_BUILDER_CODE, POLYGON_CHAIN_ID } =
        await import('@/constants/api.constants');
      const { formatBuilderCode } = await import('@/utils/clob.utils');

      const signatureType =
        kind === 'deposit_wallet' ? SignatureTypeV2.POLY_1271 : SignatureTypeV2.POLY_GNOSIS_SAFE;

      const syncClient = new ClobClient({
        host: CLOB_API_URL,
        chain: POLYGON_CHAIN_ID,
        signer: signer as any,
        creds: {
          key: creds.apiKey,
          secret: creds.secret || creds.apiSecret,
          passphrase: creds.passphrase || creds.apiPassphrase,
        },
        signatureType,
        funderAddress,
        builderConfig: {
          builderCode: formatBuilderCode(POLY_BUILDER_CODE),
        },
        useServerTime: true,
      });

      // Establishes the EOA → depositWallet mapping in Polymarket's CLOB.
      // POLY_ADDRESS = EOA (from the signer); the CLOB identifies the deposit wallet
      // via signature_type=3 + deterministic derivation from EOA.
      // Only COLLATERAL is a valid asset_type for this endpoint.
      await syncClient.updateBalanceAllowance({ asset_type: 'COLLATERAL' as any });
    } catch (e: any) {
      if (e?.response?.status === 401) {
        await resetCredentials();
        dispatch(setIsReadyToTrade(false));
      }
    }
  };

  const initializeProxy = useCallback(
    async (clientOverride?: any) => {
      const activeClient = clientOverride || client;

      if (!activeClient || isSystemInitializing || !eoaAddress) {
        return;
      }

      dispatch(setProxyInitializing(true));
      try {
        setSetupStep('prefetching');

        let tradingWalletKind: TradingWalletKind =
          (user?.tradingWalletKind as TradingWalletKind) || 'gnosis_safe';
        let proxyAddress = existingProxyAddress;

        try {
          const info = await walletApi.getProxyInfo();
          if (info?.success && info.data?.proxyAddress) {
            proxyAddress = normalizeClobAddress(info.data.proxyAddress);
            tradingWalletKind = info.data.tradingWalletKind ?? 'gnosis_safe';

            // FIX: If the registered proxy address is the same as the EOA for a deposit_wallet,
            // it's a misconfiguration. We must reset it to force derivation of the correct
            // CREATE2 deposit wallet address.
            if (
              tradingWalletKind === 'deposit_wallet' &&
              proxyAddress?.toLowerCase() === eoaAddress?.toLowerCase()
            ) {
              console.warn(
                '[ProxyInit] Proxy address matches EOA for deposit_wallet. Forcing re-derivation.'
              );
              proxyAddress = undefined;
            }

            dispatch(
              updateUser({
                proxyWalletAddress: proxyAddress,
                tradingWalletKind,
              })
            );
          }
        } catch (e: any) {
          if (e?.response?.status === 404) {
            tradingWalletKind = 'deposit_wallet';
            proxyAddress = undefined;
            dispatch(updateUser({ tradingWalletKind: 'deposit_wallet' }));
          }
        }

        // Corrupt-data guard: if the backend stored the EOA as the proxy address for a
        // deposit_wallet user (can happen from legacy registration), re-derive the real
        // deposit wallet address and patch both the backend and Redux.
        if (
          tradingWalletKind === 'deposit_wallet' &&
          proxyAddress &&
          proxyAddress.toLowerCase() === eoaAddress?.toLowerCase()
        ) {
          try {
            const derived = await activeClient.deriveDepositWalletAddress();
            proxyAddress = normalizeClobAddress(derived);
            await walletApi.registerProxy({
              proxyAddress,
              deploymentTxHash: 'ALREADY_DEPLOYED',
              status: 'deployed',
              tradingWalletKind,
            });
            dispatch(updateUser({ proxyWalletAddress: proxyAddress, tradingWalletKind }));
          } catch {
            proxyAddress = undefined; // fall through to full deploy below
          }
        }

        let provisionedCreds = credentials;

        if (!provisionedCreds) {
          provisionedCreds = await refetchStatus();
        }

        // Read binding from localStorage directly — Redux may not yet reflect the hydration effect.
        const effectiveBinding =
          user?.clobCredentialBinding ?? (user?.id ? readStoredClobBinding(user.id) : null);

        if (
          tradingWalletKind === 'deposit_wallet' &&
          provisionedCreds &&
          effectiveBinding !== 'deposit_wallet'
        ) {
          await clobApi.deleteCredentials().catch(() => {});
          if (user?.id) clearStoredClobBinding(user.id);
          provisionedCreds = null;
          dispatch(updateUser({ clobCredentials: null as any, clobCredentialBinding: undefined }));
          dispatch(setSetupRequested(true));
        }

        let isActuallyDeployed = false;
        if (proxyAddress) {
          try {
            if (typeof activeClient.getDeployed === 'function') {
              isActuallyDeployed =
                tradingWalletKind === 'deposit_wallet'
                  ? await activeClient.getDeployed(proxyAddress, 'WALLET')
                  : await activeClient.getDeployed(proxyAddress);
            } else {
              const { POLYMARKET_RELAYER_URL } = await import('@/constants/api.constants');
              if (POLYMARKET_RELAYER_URL) {
                const response = await fetch(
                  `${POLYMARKET_RELAYER_URL}/deployed?address=${proxyAddress}`
                );
                const data = await response.json();
                isActuallyDeployed = !!data.deployed;
              }
            }
          } catch (e) {
            console.warn(
              '[ProxyInit] Could not verify deployment status, assuming not deployed:',
              e
            );
          }
        }

        let needsDeploy = !proxyAddress || !isActuallyDeployed;
        let needsProvisioning = !provisionedCreds;

        let needsApprovals = false;
        if (proxyAddress && isActuallyDeployed) {
          const status = await checkAllTokenApprovals(proxyAddress);
          needsApprovals = !status.allApproved;
        } else {
          needsApprovals = true;
        }

        if (!needsDeploy && !needsProvisioning && !needsApprovals) {
          dispatch(setIsReadyToTrade(true));
          // For deposit_wallet users: always call updateBalanceAllowance on every login.
          // This is idempotent and ensures the EOA→depositWallet mapping exists in Polymarket's
          // CLOB for users who signed up before this call was added to the provisioning flow.
          if (proxyAddress && tradingWalletKind === 'deposit_wallet' && provisionedCreds) {
            await syncCollateralBalance(proxyAddress, provisionedCreds, tradingWalletKind);
          }
          setSetupStep('idle');
          return proxyAddress;
        }

        let didDeploy = false;

        if (needsDeploy) {
          didDeploy = true;
          setSetupStep('deploying');
          // toast.info(MESSAGES.TOAST.ESTABLISHING_WALLET);

          let deployRes: any;
          let deploymentTxHash = '';

          if (tradingWalletKind === 'deposit_wallet') {
            if (typeof activeClient.deployDepositWallet !== 'function') {
              throw new Error(
                'Deposit wallet deployment requires @polymarket/builder-relayer-client >= 0.0.9'
              );
            }
            const predicted = await activeClient.deriveDepositWalletAddress();
            let retries = 0;
            const MAX_RETRIES = 10;

            while (retries < MAX_RETRIES) {
              try {
                console.info('[ProxyInit] deployDepositWallet: requesting signature…', {
                  attempt: retries + 1,
                  predicted,
                });
                deployRes = await activeClient.deployDepositWallet();
                console.info('[ProxyInit] deployDepositWallet: signature returned ✓');

                // CRITICAL: the relayer client's wait()/pollUntilState returns
                // `undefined` when the WALLET-CREATE transaction fails or times
                // out — it never throws. Treating that as success used to record
                // an address that was never deployed, permanently breaking the
                // relayer registry lookups (approvals fail "not registered").
                const result =
                  typeof deployRes?.wait === 'function' ? await deployRes.wait() : deployRes;
                if (!result) {
                  throw new Error(
                    'Deposit wallet deployment transaction failed or timed out on the relayer'
                  );
                }
                deploymentTxHash = result?.transactionHash || '';
                break; // success — transaction actually mined
              } catch (error: any) {
                const msg = error?.message?.toLowerCase() ?? '';
                if (msg.includes('already') || msg.includes('exists')) {
                  deploymentTxHash = 'ALREADY_DEPLOYED';
                  break;
                } else {
                  retries++;
                  if (retries >= MAX_RETRIES) {
                    throw error;
                  }
                  console.warn(
                    `[ProxyInit] deployDepositWallet failed, retrying... (${retries}/${MAX_RETRIES})`,
                    error
                  );
                  await new Promise((r) => setTimeout(r, 2000)); // wait 2 seconds between attempts
                }
              }
            }
            proxyAddress = normalizeClobAddress(predicted);
          } else {
            try {
              console.info('[ProxyInit] deploy (Safe): requesting signature…');
              deployRes = await activeClient.deploy();
              console.info('[ProxyInit] deploy (Safe): signature returned ✓');
            } catch (error: any) {
              if (
                error.message?.toLowerCase().includes('exists') ||
                error.message?.includes('already deployed')
              ) {
                const safeFactory = activeClient.contractConfig?.SafeContracts?.SafeFactory;
                const safeAddr =
                  proxyAddress || (safeFactory ? deriveSafe(eoaAddress, safeFactory) : undefined);

                if (!safeAddr)
                  throw new Error('Safe already exists but could not determine address.');
                deployRes = { proxyAddress: safeAddr, transactionHash: 'ALREADY_DEPLOYED' };
              } else {
                throw error;
              }
            }

            const result =
              typeof deployRes?.wait === 'function' ? await deployRes.wait() : deployRes;
            proxyAddress = result?.proxyAddress || result?.proxy || result?.address;
            if (proxyAddress) proxyAddress = normalizeClobAddress(proxyAddress);
            deploymentTxHash = result?.transactionHash || 'ALREADY_DEPLOYED';
          }

          if (!proxyAddress) throw new Error('Wallet deployment failed: No address returned.');

          setSetupStep('registering');
          await walletApi.registerProxy({
            proxyAddress,
            deploymentTxHash: deploymentTxHash || 'ALREADY_DEPLOYED',
            status: 'deployed',
            tradingWalletKind,
          });

          dispatch(
            updateUser({
              proxyWalletAddress: proxyAddress,
              tradingWalletKind,
            })
          );
          needsApprovals = true;
          needsProvisioning = true;
        }

        if (needsApprovals && proxyAddress) {
          setSetupStep('approving');
          if (didDeploy) await new Promise((r) => setTimeout(r, 2000));

          const allOk = await ensureAllTokenApprovals(
            proxyAddress,
            activeClient,
            tradingWalletKind
          );
          if (!allOk) throw new Error('Token approvals were rejected or failed.');
        }

        if (needsProvisioning || !provisionedCreds) {
          setSetupStep('provisioning');
          if (didDeploy) await new Promise((r) => setTimeout(r, 1000));

          provisionedCreds = await provisionCredentials(proxyAddress, { tradingWalletKind });
          if (!provisionedCreds) throw new Error('Failed to provision trading credentials.');
        }

        dispatch(setIsReadyToTrade(true));

        if (proxyAddress && provisionedCreds) {
          await syncCollateralBalance(proxyAddress, provisionedCreds, tradingWalletKind);
        }

        setSetupStep('complete');
        toast.success(MESSAGES.TOAST.TRADING_ACCOUNT_INITIALIZED);
        return proxyAddress;
      } catch (error: any) {
        if (process.env.NODE_ENV === 'development') {
          console.error('[ProxyInit] Pipeline failed:', error);
        }
        setSetupStep('error');
        const msg = error.message || 'Trading initialization failed.';
        dispatch(setProxyError(msg));
        toast.error(msg);
        throw error;
      } finally {
        dispatch(setProxyInitializing(false));
      }
    },
    [
      client,
      isSystemInitializing,
      eoaAddress,
      existingProxyAddress,
      user?.tradingWalletKind,
      user?.clobCredentialBinding,
      credentials,
      signer,
      dispatch,
      provisionCredentials,
      refetchStatus,
      checkAllTokenApprovals,
      ensureAllTokenApprovals,
    ]
  );

  const syncBalance = useCallback(async () => {
    const kind = (user?.tradingWalletKind as TradingWalletKind) || 'gnosis_safe';
    if (!existingProxyAddress || !credentials || !signer) return;
    await syncCollateralBalance(existingProxyAddress, credentials, kind);
  }, [existingProxyAddress, credentials, signer, user?.tradingWalletKind]);

  return {
    initializeProxy,
    syncBalance,
    isDeploying: isSystemInitializing,
    setupStep,
    setSetupStep,
  };
}
